import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useErrorContext } from '@/modules/admin/context/ErrorContext';
import Navigation from '@/shared/components/Navigation';
import { useToast } from '@/hooks/useToast';
import ToastContainer from '@/shared/components/ToastContainer';
import { captureError } from '@/utils/errorHandler';

export default function AdminErrorDetailPage() {
  const { errorId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuthContext();
  const { errors, loadErrors, setActiveErrorId, saveScrollPosition } = useErrorContext();
  const { showToast, toasts, hideToast } = useToast();
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [filteredAppName, setFilteredAppName] = useState(null);

  // Get filters from URL to preserve state
  const statusFilter = searchParams.get('status') || '';
  const sourceFilter = searchParams.get('source') || '';
  const searchQuery = searchParams.get('search') || '';
  const appIdFilter = searchParams.get('appId') || '';
  const page = searchParams.get('page') || '1';

  // Fetch app name when filtering by appId
  useEffect(() => {
    if (appIdFilter) {
      authFetch(`/api/admin/app?appId=${appIdFilter}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.name) {
            setFilteredAppName(data.name);
          } else {
            setFilteredAppName(null);
          }
        })
        .catch(() => setFilteredAppName(null));
    } else {
      setFilteredAppName(null);
    }
  }, [appIdFilter, authFetch]);

  // Find error from context or fetch if needed
  useEffect(() => {
    const findError = async () => {
      setLoading(true);
      
      // First try to find in context
      let foundError = errors.find(e => e.id === errorId);
      
      if (!foundError) {
        // If not in context, try to load errors with current filters
        try {
          const data = await loadErrors({
            status: statusFilter,
            source: sourceFilter,
            search: searchQuery,
            appId: appIdFilter,
            page: parseInt(page, 10),
          });
          // Find error from the loaded data
          foundError = (data?.errors || []).find(e => e.id === errorId);
        } catch (err) {
          captureError(err, {
            action: 'loadErrorDetails',
            component: 'AdminErrorDetailPage',
            extra: { errorId },
          });
          console.error('Error loading errors:', err);
        }
      }
      
      if (foundError) {
        setError(foundError);
      } else {
        // Error not found - show error state
        setError(null);
      }
      
      setLoading(false);
    };

    findError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorId]);

  // Update error when errors array changes (e.g., after status update)
  useEffect(() => {
    const foundError = errors.find(e => e.id === errorId);
    if (foundError) {
      setError(foundError);
    }
  }, [errors, errorId]);

  const handleBack = () => {
    // Save scroll position before navigating
    saveScrollPosition();
    
    // Set active error so it gets highlighted when we return
    setActiveErrorId(errorId);
    
    // Build back URL with all query params
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (sourceFilter) params.set('source', sourceFilter);
    if (searchQuery) params.set('search', searchQuery);
    if (appIdFilter) params.set('appId', appIdFilter);
    if (page !== '1') params.set('page', page);
    
    const queryString = params.toString();
    navigate(`/admin-portal/errors${queryString ? `?${queryString}` : ''}`);
  };

  const handleStatusChange = async (newStatus) => {
    if (!error) return;
    
    try {
      setUpdatingStatus(true);
      const response = await authFetch('/api/admin/error-status', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          errorIds: [error.id],
          status: newStatus,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      showToast(`Marked as ${newStatus}`, 'success');
      
      // Reload errors to get updated status
      await loadErrors({
        status: statusFilter,
        source: sourceFilter,
        search: searchQuery,
        appId: appIdFilter,
        page: parseInt(page, 10),
      });
      
      // Update local error state
      const updatedError = errors.find(e => e.id === errorId);
      if (updatedError) {
        setError(updatedError);
      }
    } catch (err) {
      captureError(err, {
        action: 'handleUpdateStatus',
        component: 'AdminErrorDetailPage',
        extra: { errorId, newStatus },
      });
      console.error('Error updating status:', err);
      showToast('Failed to update status', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'acknowledged':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'resolved':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'vercel':
        return 'bg-black text-white';
      case 'sentry':
        return 'bg-purple-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const getEnvironmentBadge = (environment) => {
    switch (environment) {
      case 'production':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'development':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', { 
      dateStyle: 'medium', 
      timeStyle: 'short' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
            <p className="text-gray-600">Loading error details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Error Not Found</h1>
            <p className="text-gray-600 mb-6">The error you're looking for doesn't exist or has been deleted.</p>
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 transition-colors font-medium"
            >
              ← Back to Error List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="mb-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Error List
          </button>
          
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">Error Details</h1>
            <div className="flex gap-2">
              {error.status === 'new' && (
                <button
                  onClick={() => handleStatusChange('acknowledged')}
                  disabled={updatingStatus}
                  className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors font-medium text-sm disabled:opacity-50"
                >
                  {updatingStatus ? 'Updating...' : 'Acknowledge'}
                </button>
              )}
              {error.status !== 'resolved' && (
                <button
                  onClick={() => handleStatusChange('resolved')}
                  disabled={updatingStatus}
                  className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium text-sm disabled:opacity-50"
                >
                  {updatingStatus ? 'Updating...' : 'Resolve'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* App Filter Banner */}
        {appIdFilter && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-blue-600 font-medium">📱 Filtering by app:</span>
              <span className="text-blue-800 font-semibold">
                {filteredAppName || 'Unknown app'}
              </span>
              <Link
                to={`/admin-portal/app/${appIdFilter}`}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                View app →
              </Link>
            </div>
          </div>
        )}

        {/* Error Details Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          {/* Header Section */}
          <div className="border-b border-gray-200 pb-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <span className={`px-3 py-1 rounded text-sm font-medium ${getSourceBadge(error.source)}`}>
                {error.source}
              </span>
              {error.environment && (
                <span className={`px-3 py-1 rounded text-sm font-medium border ${getEnvironmentBadge(error.environment)}`}>
                  {error.environment}
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadge(error.status)}`}>
                {error.status}
              </span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {error.path || 'Unknown path'}
            </h2>
            <p className="text-sm text-gray-500">
              {formatTimestamp(error.createdAt)}
            </p>
          </div>

          {/* Error Message */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Error Message</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <pre className="text-sm text-red-900 whitespace-pre-wrap break-words font-mono">
                {error.message || 'No message provided'}
              </pre>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Request ID</p>
              <p className="text-sm font-mono text-gray-900 bg-gray-50 p-2 rounded border border-gray-200">
                {error.requestId || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Source Identifier</p>
              <p className="text-sm font-mono text-gray-900 bg-gray-50 p-2 rounded border border-gray-200">
                {error.sourceIdentifier || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Environment</p>
              <p className="text-sm text-gray-900">
                {error.environment ? (
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getEnvironmentBadge(error.environment)}`}>
                    {error.environment}
                  </span>
                ) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Branch</p>
              <p className="text-sm font-mono text-gray-900 bg-gray-50 p-2 rounded border border-gray-200">
                {error.branch || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">App</p>
              {error.appId ? (
                <Link
                  to={`/admin-portal/app/${error.appId}`}
                  className="text-sm text-zapt-purple hover:underline"
                >
                  {error.appName || 'Unknown'}
                </Link>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  {error.sourceIdentifier || 'Unlinked'}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Level</p>
              <p className="text-sm text-gray-900 capitalize">
                {error.level || 'error'}
              </p>
            </div>
          </div>

          {/* Source URL Link */}
          {error.sourceUrl && (
            <div className="border-t border-gray-200 pt-6">
              <a
                href={error.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                <span>View in {error.source === 'vercel' ? 'Vercel' : error.source === 'sentry' ? 'Sentry' : 'Source'}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              {error.source === 'sentry' && (
                <p className="mt-3 text-sm text-gray-500">
                  💡 <strong>Tip:</strong> Delete this issue in Sentry after resolving it to be notified if it recurs.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <ToastContainer toasts={toasts} onCloseToast={hideToast} />
    </div>
  );
}
